import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const SSO_ERROR_MESSAGES = {
  bad_state: 'The sign-in request expired or was tampered with. Please try again.',
  state_expired: 'The sign-in request expired. Please try again.',
  bad_nonce: 'A security check failed. Please try again.',
  bad_signature: 'Your identity provider returned an invalid token.',
  issuer_mismatch: 'Your identity provider issuer does not match our configuration.',
  audience_mismatch: 'This application is not registered correctly with your identity provider.',
  tenant_mismatch: 'The Microsoft directory you signed in to does not match the one configured for your organization.',
  expired_token: 'Your identity token expired in transit. Please try again.',
  domain_not_allowed: 'Your email domain is not authorized for this organization.',
  user_not_found: 'Your account has not been invited yet. Please ask your administrator to add you.',
  account_inactive: 'This account is no longer active. Please contact your administrator.',
  org_suspended: 'Your organization’s access has been suspended. Please contact Precision Compliance Group.',
  token_exchange_failed: 'Microsoft rejected the sign-in. Please try again.',
  token_verification_failed: 'Your sign-in token could not be verified. Please try again.',
  sso_failed: 'Single sign-on failed. Please try again or sign in with your password.',
  session_error: 'A session error occurred. Please try again.',
};

// SOC 2 SSO — accept ?return_to= so other apps in the platform (Mandate
// Tracker today, more soon) can hand the user off to Core for login and
// receive them back at a specific URL after MFA. Strict whitelist:
// must be HTTPS to a *.precisioncompliancegroup.com host (or localhost
// in dev) — anything else is dropped to prevent open-redirect abuse.
function safeReturnTo(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const isHttps = u.protocol === 'https:';
    const isLocal = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
    const isPCG = u.hostname.endsWith('.precisioncompliancegroup.com');
    if ((isHttps || (isLocal && process.env.NODE_ENV !== 'production')) && (isPCG || isLocal)) {
      return u.toString();
    }
  } catch {
    // malformed URL — ignore
  }
  return null;
}

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = useMemo(() => safeReturnTo(searchParams.get('return_to')), [searchParams]);

  // SOC 2 SSO — if the user is already authenticated and arrives at /login
  // because a sister app (Mandate / Knowledge / Scrubber) redirected them
  // here for SSO, send them back without making them log in again. Without
  // this check, a logged-in user lands on the login form, types nothing,
  // and the SSO trip looks broken.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
          method: 'GET', credentials: 'include',
        });
        const data = await res.json();
        if (cancelled) return;
        if (data?.success && data?.user) {
          window.location.replace(returnTo || '/');
        }
      } catch { /* show form on session-check failure */ }
    })();
    return () => { cancelled = true; };
  }, [returnTo]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Federation discovery — looks up whether this email belongs to an org
  // whose identity_mode is federated/hybrid. Runs in the background on a
  // debounce so it never gates typing. Strict 'local' default means
  // existing customers see no change in their sign-in screen.
  const [identityMode, setIdentityMode] = useState('local');
  const [orgName, setOrgName] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const discoverTimer = useRef(null);

  // Surface a friendly error if we just bounced back from /sso/azure/callback
  useEffect(() => {
    const code = searchParams.get('sso_error');
    if (code) {
      setError(SSO_ERROR_MESSAGES[code] || `Sign-in failed (${code}).`);
    }
  }, [searchParams]);

  useEffect(() => {
    // Reset mode whenever the email changes, then debounce a lookup.
    setIdentityMode('local');
    setOrgName(null);
    if (discoverTimer.current) clearTimeout(discoverTimer.current);
    if (!email || !email.includes('@')) return undefined;
    discoverTimer.current = setTimeout(async () => {
      setDiscovering(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/discover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        if (data?.mode === 'federated' || data?.mode === 'hybrid') {
          setIdentityMode(data.mode);
          setOrgName(data.organization_name || null);
        }
      } catch {
        // Silent — fall back to local password path on any error.
      }
      setDiscovering(false);
    }, 350);
    return () => { if (discoverTimer.current) clearTimeout(discoverTimer.current); };
  }, [email]);

  const startFederatedSignIn = () => {
    const base = process.env.REACT_APP_API_URL || '';
    const params = new URLSearchParams();
    params.set('email', email);
    if (returnTo) params.set('return_to', returnTo);
    window.location.href = `${base}/api/auth/sso/azure/start?${params.toString()}`;
  };
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactTopic, setContactTopic] = useState('Login or access');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitMessage, setContactSubmitMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (identityMode === 'federated') {
      startFederatedSignIn();
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Login failed');
        setLoading(false);
        return;
      }

      // SOC 2 CC6.1 — MFA flows.
      // (a) User has MFA enabled → server sent a challenge_token; finish
      //     login on the MFA challenge page. Carry return_to forward.
      if (data.mfa_required && data.challenge_token) {
        navigate('/mfa-challenge', {
          state: { challenge_token: data.challenge_token, email, return_to: returnTo },
        });
        return;
      }
      // (b) User is non-test and has not enrolled yet → forced enrollment.
      //     Stash return_to in sessionStorage since MfaEnroll has no
      //     router state input (it's reachable via direct navigation too).
      if (data.mfa_enrollment_required) {
        if (returnTo) sessionStorage.setItem('mfa_return_to', returnTo);
        navigate('/mfa-enroll');
        return;
      }

      if (setUser) setUser(data.user);
      window.location.href = returnTo || '/';
    } catch (err) {
      setError('Unable to connect. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });
      const result = await response.json();
      setForgotPasswordMessage(result.message);
      if (result.success) {
        setForgotPasswordEmail('');
        setTimeout(() => setShowForgotPassword(false), 3000);
      }
    } catch {
      setForgotPasswordMessage('An error occurred. Please try again.');
    }
  };

  const handleContactSupport = async (e) => {
    e.preventDefault();
    setContactSubmitMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/contact-support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: contactEmail, topic: contactTopic, message: contactMessage }),
        credentials: 'include'
      });
      const data = await res.json();
      setContactSubmitMessage(data.message || (data.success ? 'Sent.' : 'Something went wrong.'));
      if (data.success) {
        setContactEmail(''); setContactTopic('Login or access'); setContactMessage('');
        setTimeout(() => { setShowContactSupport(false); setContactSubmitMessage(''); }, 2500);
      }
    } catch {
      setContactSubmitMessage('Could not send. Try again or email us directly.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#f8fafc]">

      {/* Ambient tonal glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[460px] opacity-40"
             style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        <div className="absolute bottom-1/4 right-1/3 w-[520px] h-[380px] opacity-40"
             style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] opacity-30"
             style={{ background: 'radial-gradient(ellipse, rgba(16,185,129,0.08) 0%, transparent 70%)', filter: 'blur(90px)' }} />
      </div>

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(rgba(15,23,42,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 w-full max-w-sm px-6">

        {/* Brand — the PCG lighthouse mark above the wordmark */}
        <div className="text-center mb-10">
          <svg className="w-14 h-14 mx-auto mb-4" viewBox="0 0 512 512" role="img" aria-label="Precision Compliance Group">
            <defs>
              <linearGradient id="pcgLoginMark" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#F79A3E" />
                <stop offset="1" stopColor="#E5601F" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#pcgLoginMark)" strokeWidth="26" strokeLinecap="round">
              <path d="M148 88 A 150 150 0 0 0 148 216" />
              <path d="M364 88 A 150 150 0 0 1 364 216" />
            </g>
            <circle cx="256" cy="152" r="46" fill="url(#pcgLoginMark)" />
            <path d="M212 232 h88 l40 214 a16 16 0 0 1 -16 20 H188 a16 16 0 0 1 -16 -20 Z" fill="#0E2841" />
          </svg>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Precision Compliance Group</h1>
          <p className="text-slate-500 text-sm mt-1">Sign in to your workspace</p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-7 shadow-sm shadow-slate-200/50">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organization.com"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white transition-all"
                required
                autoFocus
              />
            </div>

            {identityMode !== 'federated' && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white transition-all"
                  required={identityMode === 'local'}
                />
              </div>
            )}

            {identityMode === 'federated' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3 text-xs text-slate-700">
                <span className="font-semibold text-slate-800">{orgName || 'Your organization'}</span> uses Microsoft sign-in. You will be redirected to your company’s Microsoft account.
              </div>
            )}
            {identityMode === 'hybrid' && (
              <button
                type="button"
                onClick={startFederatedSignIn}
                className="w-full py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center justify-center gap-2 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                  <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
                  <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
                  <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
                  <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                </svg>
                Continue with Microsoft
              </button>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-xs flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || discovering}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold rounded-xl hover:opacity-95 disabled:opacity-50 transition-opacity shadow-md shadow-orange-500/20 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </>
              ) : identityMode === 'federated' ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
                    <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
                    <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
                    <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                  </svg>
                  Continue with Microsoft
                </>
              ) : 'Sign in'}
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
            >
              Forgot password?
            </button>
            <button
              type="button"
              onClick={() => setShowContactSupport(true)}
              className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
            >
              Contact support
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[11px] text-slate-400 mt-8">
          &copy; {new Date().getFullYear()} Precision Compliance Group
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-sm w-full shadow-2xl shadow-slate-900/10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-800">Reset Password</h2>
              <button onClick={() => { setShowForgotPassword(false); setForgotPasswordMessage(''); setForgotPasswordEmail(''); }}
                className="text-slate-400 hover:text-slate-700 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <input
                type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white"
                placeholder="Enter your email" required
              />
              <button type="submit" className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold rounded-xl hover:opacity-95 transition-opacity shadow-md shadow-orange-500/20">
                Send Reset Link
              </button>
              {forgotPasswordMessage && (
                <p className={`text-xs px-3 py-2 rounded-xl ${forgotPasswordMessage.includes('sent') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {forgotPasswordMessage}
                </p>
              )}
            </form>
            <p className="text-[11px] text-slate-400 mt-3 text-center">We'll send a secure link. Expires in 1 hour.</p>
          </div>
        </div>
      )}

      {/* Contact Support Modal */}
      {showContactSupport && (
        <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-sm w-full shadow-2xl shadow-slate-900/10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-800">Contact Support</h2>
              <button onClick={() => { setShowContactSupport(false); setContactSubmitMessage(''); setContactEmail(''); setContactTopic('Login or access'); setContactMessage(''); }}
                className="text-slate-400 hover:text-slate-700 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleContactSupport} className="space-y-3">
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white"
                placeholder="Your email" required />
              <select value={contactTopic} onChange={(e) => setContactTopic(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white">
                <option value="Login or access">Login or access</option>
                <option value="Password reset">Password reset</option>
                <option value="Account or permissions">Account or permissions</option>
                <option value="Technical issue">Technical issue</option>
                <option value="Other">Other</option>
              </select>
              <textarea value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} rows={3}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:bg-white resize-none"
                placeholder="Describe your issue..." required />
              <button type="submit" className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold rounded-xl hover:opacity-95 transition-opacity shadow-md shadow-orange-500/20">
                Send Request
              </button>
              {contactSubmitMessage && (
                <p className={`text-xs ${contactSubmitMessage.includes('sent') || contactSubmitMessage.includes('soon') ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {contactSubmitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
